import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme } from '@mui/material';

import { IntroSlide, useIntroSlides } from './hooks/IntroSlidesPage.hooks';
import { BlueprintStartHere } from './molecules/BlueprintStartHere';
import { IntroSlideContent } from './molecules/IntroSlideContent';
import { IntroSlidesNavFooter } from './molecules/IntroSlidesNavFooter';

import 'locomotive-scroll/dist/locomotive-scroll.css';

const INTRO_STRINGS = appStrings.modules.intro;
const SLIDE_WIDTH = 75;

const SLIDES: IntroSlide[] = [
  {
    variant: {
      type: 'STANDARD',
      title: INTRO_STRINGS.slides.slide1.title,
      description: INTRO_STRINGS.slides.slide1.description,
      imagePath: '/images/90000.png',
    },
    startAnchor: 'middle',
    endAnchor: 'middle',
  },
  {
    variant: {
      type: 'STANDARD',
      title: INTRO_STRINGS.slides.slide2.title,
      description: INTRO_STRINGS.slides.slide2.description,
      imagePath: '/images/two-up-arrows.png',
    },
    startAnchor: 'middle',
    endAnchor: 'middle',
  },
  {
    variant: {
      type: 'STANDARD',
      title: INTRO_STRINGS.slides.slide3.title,
      description: INTRO_STRINGS.slides.slide3.description,
      imagePath: '/images/20-percent.png',
    },
    startAnchor: 'left',
    endAnchor: 'right',
  },
  {
    variant: {
      type: 'ELEMENT',
      element: ({ index, currentSlide, setCurrentSlide }) => (
        <BlueprintStartHere
          id={`slide-${index}`}
          isSlideActive={currentSlide === index}
          onSlideClick={() => setCurrentSlide(index)}
        />
      ),
    },
    startAnchor: 'right',
    endAnchor: 'left',
  },
];

export const IntroSlidesPage = () => {
  const { activeSlide, containerRef, slideRefs, bind, scrollToSlide } = useIntroSlides(SLIDES);

  return (
    <>
      <Box ref={containerRef} data-scroll-container {...bind()} sx={sliderContainerSx}>
        {/* RENDERING THE ACTUAL SLIDES */}
        {SLIDES.map((slide, index) => (
          <Box
            ref={(el: HTMLDivElement | null) => (slideRefs.current[index] = el)}
            key={`${slide.variant.type}-${index}`}
            data-scroll-section
            sx={{
              ...slideSx,
              paddingLeft: index === 0 || index === SLIDES.length - 1 ? '72px' : 0,
              // if it's an element type, then we let the element take up the full width of the viewport
              width: slide.variant.type === 'ELEMENT' ? '100svw' : `${SLIDE_WIDTH}svw`,
            }}
          >
            <IntroSlideContent
              slide={slide}
              index={index}
              slidesLength={SLIDES.length}
              currentSlide={activeSlide}
              setCurrentSlide={scrollToSlide}
            />
          </Box>
        ))}
      </Box>

      {/* NAVIGATION FOOTER */}
      <IntroSlidesNavFooter
        currentSlide={activeSlide}
        setCurrentSlide={scrollToSlide}
        slides={SLIDES}
      />
    </>
  );
};

const sliderContainerSx: SxProps<Theme> = {
  overflow: 'hidden',
  touchAction: 'none',
  display: 'flex',
  userSelect: 'none',
  width: '100%',
  height: '100svh',
  position: 'relative',
  scrollbarWidth: 'none',
} as const;

const slideSx: SxProps<Theme> = {
  flexShrink: 0,
  margin: 0,
  padding: 0,
} as const;
